import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { Button, Labeled, Modal, TextInput } from '@tes/ui';
import { API_SCOPES } from '@tes/domain';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { useFormatDate } from '../lib/useSettings';
import type { ApiScope } from '../lib/types';

export default function ApiKeysPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['api-keys'], queryFn: api.listApiKeys });
  const [showNew, setShowNew] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const fmt = useFormatDate();

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h1 className="text-lg font-semibold">API Keys</h1>
        <Button onClick={() => setShowNew(true)}>
          <Plus size={16} className="mr-1" /> New key
        </Button>
      </div>

      {freshKey && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="mb-1 text-sm font-medium text-emerald-800">
            Copy this key now — it won't be shown again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white px-2 py-1 font-mono text-xs">{freshKey}</code>
            <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(freshKey)} aria-label="Copy key">
              <Copy size={14} />
            </Button>
          </div>
        </div>
      )}

      {(error || revoke.error) && (
        <p className="mb-3 text-sm text-red-600">
          {messageOf(error ?? revoke.error, 'Something went wrong')}
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : !data || data.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
          No API keys. Create one for n8n to authenticate.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2">Scopes</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">Last used</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((k) => (
                <tr key={k.id} className={k.revokedAt ? 'opacity-40' : ''}>
                  <td className="px-4 py-2 font-medium">{k.label}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500">{fmt.date(k.createdAt)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500">
                    {k.lastUsedAt ? fmt.dateTime(k.lastUsedAt) : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!k.revokedAt && (
                      <button
                        type="button"
                        onClick={() => revoke.mutate(k.id)}
                        disabled={revoke.isPending}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                        aria-label={`Revoke ${k.label}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewKeyDialog
          onClose={() => setShowNew(false)}
          onCreated={(key) => {
            setShowNew(false);
            setFreshKey(key);
            void qc.invalidateQueries({ queryKey: ['api-keys'] });
          }}
        />
      )}
    </div>
  );
}

function NewKeyDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string) => void }) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>(['templates:read', 'documents:write']);

  const mutation = useMutation({
    mutationFn: () => api.createApiKey(label, scopes),
    onSuccess: (r) => onCreated(r.key),
  });

  const toggle = (s: ApiScope) =>
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <Modal title="New API key" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Labeled label="Label">
          <TextInput
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            placeholder="n8n-production"
            data-autofocus
          />
        </Labeled>
        <div>
          <span className="mb-1 block text-xs font-medium text-slate-500">Scopes</span>
          <div className="space-y-1">
            {API_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggle(s)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600"
                />
                <code className="text-xs">{s}</code>
              </label>
            ))}
          </div>
        </div>
        {mutation.error && (
          <p className="text-sm text-red-600">{messageOf(mutation.error, 'Failed to create key.')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || scopes.length === 0}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
